/**
 * @fileoverview BalanceDisplay component shows budget balance information with
 * collapsible details on mobile. Displays ending balance prominently with chevron
 * indicator. Click to expand/collapse starting and current balance details.
 */

import styles from './BalanceDisplay.module.css';
import MoneyDisplay from '../common/MoneyDisplay';

/**
 * BalanceDisplay Component
 *
 * Displays budget balance information for the selected month.
 * Shows ending balance prominently, with starting and current balance below.
 * On mobile, secondary balances can be collapsed/expanded by clicking.
 *
 * Props:
 * - endingBalance (number): Ending balance for the month
 * - startingBalance (number): Starting balance for the month
 * - currentBalance (number): Current balance (running total)
 * - balanceError (string): Error message if balance fetch failed
 * - isExpanded (boolean): Whether details are expanded
 * - setIsExpanded (function): Function to toggle expanded state
 */
function BalanceDisplay({ endingBalance, startingBalance, currentBalance, balanceError, isExpanded, setIsExpanded }) {
    if (balanceError) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorText}>
                    Error loading balance
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Ending Balance - Clickable on mobile to toggle details */}
            <div
                className={styles.endingBalance}
                onClick={() => setIsExpanded(!isExpanded)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
            >
                <span>Ending: <MoneyDisplay value={endingBalance} /></span>
                <span className={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
            </div>
            {/* Starting and Current - Always visible on desktop, collapsible on mobile */}
            <div className={`${styles.secondaryBalances} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                <div className={styles.balanceItem}>
                    Starting: <MoneyDisplay value={startingBalance} />
                </div>
                <div className={styles.balanceItem}>
                    Current: <MoneyDisplay value={currentBalance} />
                </div>
            </div>
        </div>
    );
}

export default BalanceDisplay;
